import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import BasketballCourt from "@/components/BasketballCourt";
import ShotDialog from "@/components/ShotDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { SHOT_TYPE_MAP, ZONE_MAP } from "@/lib/zones";

export default function Training() {
  const { user } = useAuth();
  const isCoach = user?.role === "tecnico";
  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState(isCoach ? "" : user?.athlete_id || "");
  const [shots, setShots] = useState([]);
  const [zoneStats, setZoneStats] = useState({});
  const [activeZone, setActiveZone] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await api.get("/athletes");
      setAthletes(r.data);
      if (!isCoach && user?.athlete_id) setSelectedAthlete(user.athlete_id);
    })();
  }, [isCoach, user]);

  const refreshShots = useCallback(async (aid) => {
    if (!aid) { setShots([]); setZoneStats({}); return; }
    const r = await api.get(`/shots?athlete_id=${aid}`);
    setShots(r.data);
    // compute zone stats locally for instant heatmap feedback
    const zs = {};
    r.data.forEach((s) => {
      const z = zs[s.zone] || (zs[s.zone] = { made: 0, attempts: 0, pct: 0 });
      z.attempts += 1;
      if (s.made) z.made += 1;
    });
    Object.values(zs).forEach((z) => { z.pct = z.attempts ? Math.round((100 * z.made / z.attempts) * 10) / 10 : 0; });
    setZoneStats(zs);
  }, []);

  useEffect(() => { refreshShots(selectedAthlete); }, [selectedAthlete, refreshShots]);

  const handleZoneClick = (zone) => {
    if (!selectedAthlete) {
      toast.error("Selecione uma atleta primeiro");
      return;
    }
    setActiveZone(zone);
    setDialogOpen(true);
  };

  const handleConfirm = async ({ zone_id, shot_type, made_count, missed_count }) => {
    setDialogOpen(false);
    try {
      const res = await api.post("/shots/bulk", {
        athlete_id: selectedAthlete,
        zone: zone_id,
        shot_type,
        made_count,
        missed_count,
      });
      const total = res.data.inserted;
      toast.success(`${total} arremesso${total > 1 ? "s" : ""} registrado${total > 1 ? "s" : ""} (${made_count} acerto${made_count === 1 ? "" : "s"} / ${missed_count} erro${missed_count === 1 ? "" : "s"})`);
      refreshShots(selectedAthlete);
    } catch (e) {
      const msg = e.response?.data?.detail || "Falha ao registrar arremessos";
      toast.error(typeof msg === "string" ? msg : "Falha ao registrar arremessos");
    }
  };

  const handleDeleteShot = async (id) => {
    await api.delete(`/shots/${id}`);
    refreshShots(selectedAthlete);
  };

  const summary = useMemo(() => {
    const made = shots.filter(s => s.made).length;
    return { made, attempts: shots.length, pct: shots.length ? Math.round(100 * made / shots.length * 10) / 10 : 0 };
  }, [shots]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-zinc-500">Registro de Treino</div>
            <h1 className="font-display font-black text-5xl uppercase tracking-tight mt-1">Marcar Arremessos</h1>
            <p className="text-sm text-zinc-400 mt-2">Clique em qualquer zona da quadra para registrar acerto ou erro.</p>
          </div>
          {isCoach && (
            <div className="w-full sm:w-72">
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Atleta</label>
              <Select value={selectedAthlete} onValueChange={setSelectedAthlete}>
                <SelectTrigger className="mt-2 bg-[#141414] border-zinc-800 text-white" data-testid="select-athlete">
                  <SelectValue placeholder="Selecionar atleta" />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-zinc-800 text-white max-h-72">
                  {athletes.length === 0 && <div className="px-3 py-2 text-xs text-zinc-500">Cadastre uma atleta primeiro</div>}
                  {athletes.map((a) => (
                    <SelectItem key={a.athlete_id} value={a.athlete_id} data-testid={`option-${a.athlete_id}`}>
                      {a.name} {a.number ? `#${a.number}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          <SummaryCard label="Sessão Atual" value={`${summary.pct}%`} />
          <SummaryCard label="Acertos" value={summary.made} />
          <SummaryCard label="Total" value={summary.attempts} />
          <SummaryCard label="Restantes" value="∞" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-[#141414] border border-zinc-800 rounded-md p-6">
            <BasketballCourt
              zoneStats={zoneStats}
              onZoneClick={handleZoneClick}
              activeZone={activeZone?.id}
            />
          </div>

          <div className="bg-[#141414] border border-zinc-800 rounded-md p-6">
            <h2 className="font-display font-bold text-2xl uppercase tracking-tight mb-4">Últimos arremessos</h2>
            <div className="space-y-2 max-h-[520px] overflow-y-auto" data-testid="recent-shots">
              {shots.length === 0 && <p className="text-zinc-500 text-sm">Sem arremessos ainda.</p>}
              {shots.slice(0, 30).map((s) => (
                <div key={s.shot_id} className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-zinc-800 rounded-sm">
                  <div className="min-w-0">
                    <div className="text-sm font-bold truncate">{ZONE_MAP[s.zone]?.name || s.zone}</div>
                    <div className="text-xs text-zinc-500">{SHOT_TYPE_MAP[s.shot_type]} • {s.points} pts</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[10px] uppercase font-black tracking-wider px-2 py-1 rounded-sm ${
                        s.made ? "bg-[#22C55E]/20 text-[#22C55E]" : "bg-[#EF4444]/20 text-[#EF4444]"
                      }`}
                    >
                      {s.made ? "Acerto" : "Erro"}
                    </span>
                    <button
                      onClick={() => handleDeleteShot(s.shot_id)}
                      className="text-xs text-zinc-500 hover:text-red-400"
                      data-testid={`del-shot-${s.shot_id}`}
                      title="Remover"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <ShotDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          zone={activeZone}
          onConfirm={handleConfirm}
        />
      </div>
    </Layout>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="bg-[#141414] border border-zinc-800 rounded-md p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">{label}</div>
      <div className="font-display font-black text-3xl mt-1 tracking-tighter">{value}</div>
    </div>
  );
}
